import type React from "react";
import type { Task, TaskResult } from "@/kernel/runner";
import { summarize } from "@/kernel/runner";

export const TASK_LOOK: Record<string, { mark: string; fg: string; label: string }> = {
  passed: { mark: "✓", fg: "var(--ok)", label: "прошло" },
  failed: { mark: "✕", fg: "var(--err)", label: "упало" },
  idle: { mark: "○", fg: "var(--mut)", label: "не запуск." },
};

export function Sidebar({
  tasks,
  results,
  checksCount,
  selected,
  onSelect,
  footer,
}: {
  tasks: Task[];
  results: Record<string, TaskResult>;
  checksCount: (t: Task) => number;
  selected: number;
  onSelect: (n: number) => void;
  /** Низ панели: прогон на сервере и отправка (ServerSubmit). */
  footer?: React.ReactNode;
}) {
  const s = summarize(tasks, results);
  return (
    <aside className="flex min-h-0 flex-col border-r border-bd bg-bg" data-testid="task-sidebar">
      <div className="border-b border-bd px-4 py-3.5">
        <div className="mb-2 flex items-baseline justify-between font-mono text-[11px] text-mut">
          <span className="uppercase tracking-[.06em]">задачи</span>
          <span data-testid="passed-count">
            {s.passed} / {s.total} прошло
          </span>
        </div>
        <div className="grid gap-[3px]" style={{ gridTemplateColumns: `repeat(${Math.min(tasks.length, 14)}, minmax(0,1fr))` }} data-testid="mini-grid">
          {tasks.map((t) => {
            const st = results[String(t.n)]?.status ?? "idle";
            const look = TASK_LOOK[st]!;
            return <span key={t.n} title={`Задача ${t.n}: ${look.label}`} data-status={st} className="h-[7px] rounded-[2px]" style={{ background: st === "idle" ? "transparent" : look.fg, border: `1px solid ${st === "idle" ? "var(--bd2)" : look.fg}` }} />;
          })}
        </div>
      </div>
      <ol className="m-0 min-h-0 flex-1 list-none overflow-y-auto p-0">
        {tasks.map((t) => {
          const r = results[String(t.n)];
          const st = r?.status ?? "idle";
          const look = TASK_LOOK[st]!;
          const active = t.n === selected;
          return (
            <li key={t.n}>
              <button
                type="button"
                onClick={() => onSelect(t.n)}
                aria-current={active ? "true" : undefined}
                className={`grid w-full grid-cols-[18px_minmax(0,1fr)_auto] items-start gap-2 border-0 border-b border-bd px-4 py-2.5 text-left ${active ? "bg-accsoft" : "bg-transparent hover:bg-card2"}`}
                style={{ borderLeft: active ? "2px solid var(--acc)" : "2px solid transparent", color: "var(--ink)" }}
              >
                {/* --mut на подложке --accsoft не проходит 4.5:1 → --ink2 у выбранной строки. */}
                <span className="font-mono text-[12px]" style={{ color: st === "idle" && active ? "var(--ink2)" : look.fg }}>
                  {look.mark}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-[13px] text-ink">
                    Задача {t.n} · {t.title}
                  </span>
                  <span className="block truncate font-mono text-[11px] text-ink2">{r?.error ?? (st === "idle" ? "нет запуска" : `assert · ${checksCount(t)} проверки`)}</span>
                </span>
                <span className="font-mono text-[11px]" style={{ color: st === "idle" ? "var(--ink2)" : look.fg }}>
                  {look.label}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
      {footer}
    </aside>
  );
}
